import React, { useEffect } from "react";
import { Modal, Form, Input, Button, Row, Col, message, Select, Checkbox, Collapse } from "antd";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../../redux/createAiCredential/action"; // ✅ correct action file
import userActions from "../../../redux/user/actions"

const roleOptions = ["owner", "super admin", "developer", "master admin"];
const { Panel } = Collapse;

const CreateUserLoginForm = ({ visible, onCancel, editingRecord }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [messageApi, contextHolder] = message.useMessage();

  const { totalAllAgentsData = [], loading: reduxLoading } = useSelector(
    (state) => state?.AllAgentReducer || {}
  );

  const { messages, error, buttonLoading } = useSelector(
    (state) => state?.createAiCredentialReducer || {}
  );
  const roles = useSelector((state) => state?.userReducer?.rolesData || []);
  const modulesData = useSelector((state) => state?.userReducer?.modulesData || []);

  useEffect(() => {
    if (visible) {
      dispatch(userActions.getModules());
    }
  }, [visible, dispatch]);

  useEffect(() => {
    if (visible) {
      const formValues = {
        bankId: "IDFCIF31NPCI", // set default Bank ID
      };
      if (modulesData && modulesData.length > 0) {
        formValues.permissions = modulesData.map((mod) => ({
          moduleId: mod._id,
          module: mod.moduleName,
          read: false,
          create: false,
          update: false,
          delete: false,
        }));
      }
      form.setFieldsValue(formValues);
    }
  }, [visible, modulesData, form]);



  useEffect(() => {
    if (messages) {
      messageApi.success(messages);
      form.resetFields();
      onCancel(); // close modal
      dispatch(actions.getAllAICredentialData());
      // ✅ Clear messages from Redux after showing
      dispatch(actions.clearUserLoginMessages());
    }

    if (error) {
      messageApi.error(error);

      // ✅ Clear error after showing
      dispatch(actions.clearUserLoginMessages());
    }
  }, [messages, error]);
  const handleAccessChange = (fieldName, checked) => {
    const permissions = form.getFieldValue("permissions") || [];
    if (permissions[fieldName]) {
      permissions[fieldName].read = checked;
      permissions[fieldName].create = checked;
      permissions[fieldName].update = checked;
      permissions[fieldName].delete = checked;
      permissions[fieldName].access = checked;
      form.setFieldsValue({ permissions });
    }
  };

  const handlePermissionCheckboxChange = (fieldName, key, checked) => {
    const permissions = form.getFieldValue("permissions") || [];
    const permRow = permissions[fieldName];
    if (permRow) {
      permRow[key] = checked;
      if (permRow.create || permRow.update || permRow.delete) {
        permRow.read = true;
      } else {
        permRow.read = false;
      }
      permRow.access = permRow.read && permRow.create && permRow.update && permRow.delete;
      form.setFieldsValue({ permissions });
    }
  };

  // ✅ Updated handleSubmit
  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();

      const formattedPermissions = (values.permissions || []).map((perm) => ({
        moduleId: perm.moduleId,
        create: !!perm.create,
        update: !!perm.update,
        read: !!perm.read,
        delete: !!perm.delete,
      }));

      const hasAtLeastOnePermission = formattedPermissions.some(
        (perm) => perm.read || perm.create || perm.update || perm.delete
      );

      if (!hasAtLeastOnePermission) {
        messageApi.error("Please select at least one permission module.");
        return;
      }

      const payload = {
        orgId: values.agentId, // Assuming agentId is orgId
        name: values.name, // Deriving name from email
        email: values.email,
        password: values.password,
        role: values.role,
        permissions: formattedPermissions,
      };
      dispatch(actions.createUserLogin(payload)); // ✅ dispatch action
    } catch (err) {
      console.error("Validation Failed:", err);
      messageApi.error("Please correct the form errors.");
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  useEffect(()=>{
    dispatch(userActions.getRoles())
  },[dispatch])

  return (
    <>
      {contextHolder}
      <Modal
        title={editingRecord ? "Edit User Login" : "Add User Login"}
        visible={visible}
        onCancel={handleCancel}
        maskClosable={false}
        width={600}
        centered
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={buttonLoading}
            onClick={handleSubmit}
            style={{ backgroundColor: "#073763", borderColor: "#073763" }}
          >
            {editingRecord ? "Update" : "Submit"}
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="username"
                rules={[{ required: true, message: "Please enter name" }]}
              >
                <Input placeholder="Enter name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: "Please enter email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input placeholder="Enter email" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: !editingRecord, message: "Please enter password" }]}
              >
                <Input.Password placeholder="Enter password" />
              </Form.Item>
            </Col>
            {/* Agent Name Dropdown */}
            <Col span={12}>
              <Form.Item label="Agent Name" required>
                <Select
                  placeholder="Select Agent Name"
                  loading={reduxLoading}
                  showSearch
                  optionFilterProp="children"
                  onChange={(selectedAgentId) => {
                    const selectedAgent = totalAllAgentsData.find(
                      (agent) => agent.agentId === selectedAgentId
                    );
                    if (selectedAgent) {
                      form.setFieldsValue({
                        agentId: selectedAgent.agentId,
                      });
                    }
                  }}
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {totalAllAgentsData.map((agent) => (
                    <Select.Option key={agent.agentId} value={agent.agentId}>
                      {agent.agentName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="agentId"
                label="Agent ID"
                rules={[{ required: true, message: "Agent ID is required" }]}
              >
                <Input placeholder="Agent ID" disabled />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="role"
                label="Role"
                rules={[{ required: true, message: "Please select a role" }]}
              >
                <Select placeholder="Please select a role">
                  {roles.map((item) => (
                    <Select.Option key={item.role} value={item.role}>
                      {item.role.charAt(0).toUpperCase() + item.role.slice(1)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <hr />
              <h4 style={{ marginBottom: 16 }}>Permissions</h4>
              <div style={{ maxHeight: "350px", overflowY: "auto", paddingRight: "8px" }}>
                <Form.List name="permissions">
                  {(fields) => (
                    <Collapse>
                      {fields.map((field) => (
                        <Panel
                          key={field.key}
                          header={
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", paddingRight: "16px" }}>
                              <strong>
                                {form.getFieldValue([
                                  "permissions",
                                  field.name,
                                  "module",
                                ])}
                              </strong>
                              <div onClick={(e) => e.stopPropagation()}>
                                <Form.Item
                                  name={[field.name, "access"]}
                                  valuePropName="checked"
                                  style={{ marginBottom: 0 }}
                                >
                                  <Checkbox
                                    onChange={(e) => handleAccessChange(field.name, e.target.checked)}
                                  >
                                    Access
                                  </Checkbox>
                                </Form.Item>
                              </div>
                            </div>
                          }
                        >
                          <Row gutter={16}>
                            {["read", "create", "update", "delete"].map((perm) => (
                              <Col key={perm} span={6}>
                                <Form.Item
                                  name={[field.name, perm]}
                                  valuePropName="checked"
                                  style={{ marginBottom: 0 }}
                                >
                                  <Checkbox
                                    disabled={perm === "read"}
                                    onChange={(e) => {
                                      if (perm !== "read") {
                                        handlePermissionCheckboxChange(field.name, perm, e.target.checked);
                                      }
                                    }}
                                  >
                                    {perm.charAt(0).toUpperCase() + perm.slice(1)}
                                  </Checkbox>
                                </Form.Item>
                              </Col>
                            ))}
                          </Row>
                        </Panel>
                      ))}
                    </Collapse>
                  )}
                </Form.List>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default CreateUserLoginForm;
